import React, { useState, useEffect } from 'react';
import { useAutosave } from '../../../hooks/useAutosave';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ConflictOfInterestForm = ({ caseId, agent, onUpdate }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  useAutosave(() => save(), data);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/compliance/cases/${caseId}/conflict-of-interest`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const base = d || {};
        setData({
          disclosed_to_client: true,
          client_acknowledged: true,
          ...base,
        });
      })
      .catch(() => {});
  }, [caseId]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/conflict-of-interest?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      onUpdate && onUpdate();
    } finally { setSaving(false); }
  };

  const generateAI = async () => {
    setGenerating(true);
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/ai-conflict-of-interest?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      );
      const j = await r.json();
      const fields = j.fields || { applicable: false, nature_of_conflict: 'Not that I am aware.', how_managed: '' };
      setData(prev => ({ ...prev, ...fields }));
      alert('Conflict-of-Interest evaluated by AI and saved.');
      onUpdate && onUpdate();
    } catch (e) {
      alert('AI generation failed: ' + e.message);
    } finally { setGenerating(false); }
  };

  return (
    <Card data-testid="conflict-of-interest-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="sr-only">Conflict-of-Interest Disclosure</CardTitle>
        <div className="flex gap-2 ml-auto">
          <Button onClick={generateAI} disabled={generating} variant="outline" className="border-[#D4AF37] text-[#003D5C]" data-testid="ai-conflict-btn">
            <Sparkles className="h-4 w-4 mr-2" />{generating ? 'Evaluating...' : 'Generate with AI'}
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-coi-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={data.applicable || false} onChange={(e) => setData({ ...data, applicable: e.target.checked })} data-testid="coi-applicable" />
          <span className="text-sm font-semibold">A conflict of interest exists for this case</span>
        </label>
        {data.applicable && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Nature of conflict</label>
              <textarea rows="3" value={data.nature_of_conflict || ''} onChange={(e) => setData({ ...data, nature_of_conflict: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="coi-nature" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">How is the conflict managed / mitigated?</label>
              <textarea rows="3" value={data.how_managed || ''} onChange={(e) => setData({ ...data, how_managed: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="coi-how-managed" />
            </div>
          </>
        )}
        <div className="flex flex-col gap-2 pt-3 border-t">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.disclosed_to_client || false} onChange={(e) => setData({ ...data, disclosed_to_client: e.target.checked })} data-testid="coi-disclosed" />
            <span className="text-sm">Disclosed to client in writing</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.client_acknowledged || false} onChange={(e) => setData({ ...data, client_acknowledged: e.target.checked })} data-testid="coi-acknowledged" />
            <span className="text-sm">Client acknowledged the disclosure</span>
          </label>
          <label className="flex items-center gap-2 pt-2 border-t mt-2">
            <input type="checkbox" checked={data.completed || false} onChange={(e) => setData({ ...data, completed: e.target.checked })} data-testid="coi-completed" />
            <span className="text-sm font-semibold">Mark as Complete</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
};


// =============================================================
// Advisor Notes (per-case advisor reasoning / pre-sale notes)
// =============================================================
